import { useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { Check, MoreVertical, GripVertical, Info } from 'lucide-react';
import type { WorkspaceSortDir } from '@/components/workspace/useTableSort';
import type { ColumnDropPosition } from '@/components/workspace/useColumnOrder';
import {
  readMovableHeaderGeometry,
  resolveColumnInsertTarget,
} from '@/components/workspace/resolveColumnDropPosition';
import { workspaceDropTargetClass } from '@/components/workspace/workspaceDragStyles';
import { workspaceTableClass } from '@/components/workspace/WorkspaceSection';
import { setWorkspaceColumnDragImage } from '@/components/workspace/workspaceColumnDragImage';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/HoverCard';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import {
  isColumnMenuOptionSelected,
  type WorkspaceColumnMenuAction,
} from '@/components/workspace/workspaceColumnMenu';

const DRAG_DATA_KEY = 'text/workspace-column-id';
const DRAGGING_ATTR = 'data-workspace-dragging';

/** Header-only 1px edge; resize hit-testing lives in `useWorkspaceFrozenLayout`. */
export const WORKSPACE_COLUMN_HEADER_EDGE_CLASS =
  'relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-[#D1D5DB] after:content-[""]';

const COLUMN_MENU_OPTIONS: { id: WorkspaceColumnMenuAction; label: string }[] = [
  { id: 'asc', label: 'Ascending' },
  { id: 'desc', label: 'Descending' },
  { id: 'freeze', label: 'Freeze column' },
];

function WorkspaceColumnMenu({
  label,
  columnId,
  sortBy,
  sortDir,
  onSort,
  onFreeze,
  isFrozen = false,
}: {
  label: string;
  columnId: string;
  sortBy?: string;
  sortDir?: WorkspaceSortDir;
  onSort: (columnId: string, dir?: WorkspaceSortDir) => void;
  onFreeze?: (columnId: string) => void;
  isFrozen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const defaultItemRef = useRef<HTMLButtonElement>(null);

  const runAction = (action: WorkspaceColumnMenuAction) => {
    if (action === 'freeze') {
      onFreeze?.(columnId);
    } else {
      onSort(columnId, action);
    }
    setOpen(false);
  };

  const stopDrag = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          draggable={false}
          onClick={stopDrag}
          onMouseDown={stopDrag}
          onPointerDown={stopDrag}
          aria-label={`${label} column menu`}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex shrink-0 cursor-pointer items-center rounded text-foreground-muted transition-colors hover:text-foreground"
        >
          <MoreVertical className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="z-[80] w-44 rounded-xl border-border p-1.5 shadow-lg normal-case tracking-normal"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          defaultItemRef.current?.focus();
        }}
      >
        <div role="menu" aria-label={`${label} column actions`} className="flex flex-col">
          {(onFreeze
            ? COLUMN_MENU_OPTIONS
            : COLUMN_MENU_OPTIONS.filter((option) => option.id !== 'freeze')
          ).map((option, index) => {
            const selected = isColumnMenuOptionSelected(option.id, {
              columnId,
              sortBy,
              sortDir,
              isFrozen,
            });
            return (
              <button
                key={option.id}
                ref={index === 0 ? defaultItemRef : undefined}
                type="button"
                role={option.id === 'freeze' ? 'menuitemcheckbox' : 'menuitem'}
                aria-checked={selected ? true : option.id === 'freeze' ? false : undefined}
                onClick={() => runAction(option.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-body-sm font-normal normal-case tracking-normal text-foreground hover:bg-surface-muted',
                  selected ? 'bg-surface-muted font-medium' : ''
                )}
              >
                <span className="flex w-3.5 shrink-0 items-center justify-center" aria-hidden>
                  {selected ? (
                    <Check className="h-3.5 w-3.5 text-foreground-muted" />
                  ) : null}
                </span>
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function WorkspaceColumnHeader({
  label,
  columnId,
  sortBy,
  sortDir,
  onSort,
  onFreeze,
  sortable = true,
  movable = true,
  onDragStartColumn,
  onDragHoverColumn,
  onDropColumn,
  onDragEndColumn,
  activeDragColumnId = null,
  hoverInsertIndex = null,
  isDragged = false,
  isDropTarget = false,
  dropPosition = null,
  isFrozen = false,
  className,
  style,
  minWidth,
  info,
}: {
  label: string;
  columnId: string;
  sortBy?: string;
  sortDir?: WorkspaceSortDir;
  onSort?: (columnId: string, dir?: WorkspaceSortDir) => void;
  onFreeze?: (columnId: string) => void;
  sortable?: boolean;
  movable?: boolean;
  onDragStartColumn?: (columnId: string) => void;
  onDragHoverColumn?: (
    targetId: string,
    position: ColumnDropPosition,
    clientX?: number,
    insertIndex?: number
  ) => void;
  onDropColumn?: (targetId: string, position: ColumnDropPosition) => void;
  onDragEndColumn?: () => void;
  activeDragColumnId?: string | null;
  hoverInsertIndex?: number | null;
  isDragged?: boolean;
  isDropTarget?: boolean;
  dropPosition?: ColumnDropPosition | null;
  isFrozen?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Resize floor in px; read by the column resizer via `data-workspace-min-width`. */
  minWidth?: number;
  /** Explanation shown from an ⓘ button next to the label. */
  info?: string;
}) {
  const isActive = sortable && sortBy === columnId;

  const resolveFromPointer = (clientX: number, th: HTMLTableCellElement) => {
    const row = th.parentElement;
    const table = th.closest('table');
    const draggedId =
      activeDragColumnId || table?.getAttribute(DRAGGING_ATTR) || null;
    if (!row || !draggedId) return null;
    const headers = readMovableHeaderGeometry(row);
    return resolveColumnInsertTarget(clientX, headers, draggedId, hoverInsertIndex);
  };

  return (
    <th
      data-workspace-col-id={columnId}
      data-workspace-min-width={minWidth}
      data-workspace-movable={movable ? '' : undefined}
      style={minWidth ? { ...style, minWidth } : style}
      className={cn(
        workspaceTableClass.th,
        WORKSPACE_COLUMN_HEADER_EDGE_CLASS,
        'text-center',
        movable ? 'cursor-move' : '',
        isDragged ? 'bg-brand/10 shadow-md' : '',
        workspaceDropTargetClass(isDropTarget, dropPosition),
        className
      )}
      draggable={Boolean(movable && onDragHoverColumn)}
      onDragStart={(e) => {
        if (!movable || !onDragHoverColumn) return;
        if ((e.target as HTMLElement).closest('button')) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData(DRAG_DATA_KEY, columnId);
        e.dataTransfer.effectAllowed = 'move';
        const table = e.currentTarget.closest('table');
        if (table) table.setAttribute(DRAGGING_ATTR, columnId);
        setWorkspaceColumnDragImage(e.currentTarget, e.dataTransfer);
        onDragStartColumn?.(columnId);
      }}
      onDragOver={(e) => {
        if (!movable || !onDragHoverColumn) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const resolved = resolveFromPointer(e.clientX, e.currentTarget);
        if (!resolved) return;
        onDragHoverColumn(
          resolved.targetId,
          resolved.position,
          e.clientX,
          resolved.insertIndex
        );
      }}
      onDragEnter={(e) => {
        if (!movable || !onDragHoverColumn) return;
        e.preventDefault();
        const resolved = resolveFromPointer(e.clientX, e.currentTarget);
        if (!resolved) return;
        onDragHoverColumn(
          resolved.targetId,
          resolved.position,
          e.clientX,
          resolved.insertIndex
        );
      }}
      onDrop={(e) => {
        if (!movable || !onDropColumn) return;
        e.preventDefault();
        e.stopPropagation();
        const resolved = resolveFromPointer(e.clientX, e.currentTarget);
        const position = resolved?.position ?? dropPosition ?? 'before';
        const targetId = resolved?.targetId ?? columnId;
        onDropColumn(targetId, position);
      }}
      onDragEnd={(e) => {
        e.currentTarget.closest('table')?.removeAttribute(DRAGGING_ATTR);
        onDragEndColumn?.();
      }}
      aria-sort={
        isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : sortable ? 'none' : undefined
      }
    >
      <div className="flex items-center justify-center gap-2">
        {movable ? (
          <span
            className={cn(
              'inline-flex shrink-0 items-center text-foreground-muted/80',
              isDragged ? 'text-brand' : ''
            )}
            aria-hidden
          >
            <GripVertical className="h-4 w-4" />
          </span>
        ) : null}
        <span
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap text-foreground',
            isActive ? 'font-semibold text-brand' : ''
          )}
        >
          <span>{label}</span>
          {info ? (
            <HoverCard openDelay={150} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  className="rounded-full text-foreground-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                  aria-label={`About ${label}`}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
              </HoverCardTrigger>
              <HoverCardContent className="w-64 p-3 text-xs font-normal normal-case tracking-normal whitespace-normal text-foreground-muted">
                {info}
              </HoverCardContent>
            </HoverCard>
          ) : null}
          {sortable && onSort ? (
            <WorkspaceColumnMenu
              label={label}
              columnId={columnId}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              onFreeze={onFreeze}
              isFrozen={isFrozen}
            />
          ) : null}
        </span>
      </div>
    </th>
  );
}
